import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AppMenuitem } from './app.menuitem';
import { AuthService } from '@/core/services/auth.service';
import { RoleManager, SectionKey } from '@/core/constants/role-config';

@Component({
    selector: '[app-menu]',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: `
        <ul class="layout-menu">
            <ng-container *ngFor="let item of model; let i = index">
                <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
                <li *ngIf="item.separator" class="menu-separator"></li>
            </ng-container>
        </ul>
    `
})
export class AppMenu implements OnInit {
    /**
     * Menú lateral filtrado por permisos.
     *
     * Cada hoja declara la `section` que la habilita (ver `SECTION_PERMISSIONS` en role-config).
     * Si el rol no tiene esa sección, la entrada **no se renderiza** en vez de mostrarse y fallar
     * al entrar: así el usuario no ve opciones que no puede usar. Los grupos se podan solos cuando
     * se quedan sin hijos visibles, y los separadores duplicados que eso deja se colapsan.
     *
     * Ojo: esto es cosmético, no seguridad. Quien escriba la URL a mano lo sigue frenando
     * `RoleGuard` con el `data.roles` de la ruta (también derivado de la misma matriz).
     */
    model: MenuNode[] = [];

    constructor(private authService: AuthService) {}

    async ngOnInit(): Promise<void> {
        // `getUserRoles()` es async, así que el menú aparece cuando resuelven los roles de Cognito.
        // Si falla, se cae a un menú vacío en vez de mostrar todo: preferimos de menos que de más.
        let roles: string[] = [];
        try {
            roles = await this.authService.getUserRoles();
        } catch (error) {
            console.error('AppMenu: no se pudieron obtener los roles del usuario', error);
        }
        this.model = filterMenuByRoles(MENU_DEFINITION, roles);
    }
}

/** Nodo del menú. `section` solo va en las hojas; los grupos se deciden por sus hijos. */
interface MenuNode {
    label?: string;
    icon?: string;
    routerLink?: string[];
    items?: MenuNode[];
    separator?: boolean;
    /** Sección de `SECTION_PERMISSIONS` que habilita esta entrada. Sin ella, la entrada es pública. */
    section?: SectionKey;
}

/**
 * Poda el árbol según los roles:
 *  - hoja con `section` → se queda solo si el rol tiene esa sección;
 *  - hoja sin `section` → siempre visible (hoy: los tres dashboards, que el Excel no cubre);
 *  - grupo → se queda solo si le sobrevive al menos un hijo;
 *  - separadores → se descartan los que quedan al inicio, al final o pegados a otro.
 */
function filterMenuByRoles(nodes: MenuNode[], roles: string[]): MenuNode[] {
    const kept: MenuNode[] = [];

    for (const node of nodes) {
        if (node.separator) {
            kept.push(node);
            continue;
        }

        if (node.items?.length) {
            const items = filterMenuByRoles(node.items, roles);
            if (items.length) kept.push({ ...node, items });
            continue;
        }

        if (!node.section || RoleManager.canAccessSection(roles, node.section)) kept.push(node);
    }

    return collapseSeparators(kept);
}

function collapseSeparators(nodes: MenuNode[]): MenuNode[] {
    const out = nodes.filter((node, i) => !node.separator || (i > 0 && !nodes[i - 1]?.separator));
    while (out.length && out[0].separator) out.shift();
    while (out.length && out[out.length - 1].separator) out.pop();
    return out;
}

/**
 * Definición del menú. Es una constante a nivel de módulo (no un campo de la clase) para que el
 * filtrado sea una función pura y testeable sin instanciar el componente.
 */
const MENU_DEFINITION: MenuNode[] = [
    // {
    //     label: 'Visión General',
    //     icon: 'pi pi-home',
    //     items: [
    //         // Sin `section`: los dashboards no están en la matriz de permisos del Excel.
    //         { label: 'Transacciones', icon: 'pi pi-fw pi-arrow-right-arrow-left', routerLink: ['/'] },
    //         { label: 'Metas', icon: 'pi pi-fw pi-flag', routerLink: ['/dashboard-ecommerce'] },
    //         { label: 'Operación', icon: 'pi pi-fw pi-sitemap', routerLink: ['/dashboard-banking'] }
    //     ]
    // },
    // { separator: true },
    {
        label: 'Nuestros Clientes',
        icon: 'pi pi-address-book',
        items: [
            {
                label: 'Organizaciones',
                icon: 'pi pi-fw pi-building',
                items: [
                    { label: 'Lista', icon: 'pi pi-fw pi-list', routerLink: ['/pages/organizations'], section: 'ORGANIZATIONS_READ' },
                    { label: 'Crear', icon: 'pi pi-fw pi-plus', routerLink: ['/pages/organizations/create'], section: 'ORGANIZATIONS_WRITE' }
                ]
            },
            {
                label: 'Clientes',
                icon: 'pi pi-fw pi-users',
                items: [
                    { label: 'Lista', icon: 'pi pi-fw pi-list', routerLink: ['/pages/clients-ares'], section: 'CLIENTS_READ' },
                    { label: 'Actualizacion de documentos', icon: 'pi pi-fw pi-folder-open', routerLink: ['/pages/clients-ares/documents'], section: 'CLIENTS_DOCUMENTS_REVIEW' },
                    { label: 'Actualizacion de clientes', icon: 'pi pi-fw pi-user-edit', routerLink: ['/pages/clients-ares/updates'], section: 'CLIENTS_REVIEW' },
                    { label: 'Crear', icon: 'pi pi-fw pi-plus', routerLink: ['/pages/clients-ares/create'], section: 'CLIENTS_WRITE' }
                ]
            },
            {
                label: 'Prospectos',
                icon: 'pi pi-fw pi-user-plus',
                items: [{ label: 'Lista', icon: 'pi pi-fw pi-list', routerLink: ['/pages/prospects-ares'], section: 'PROSPECTS_READ' }]
            }
        ]
    },
    { separator: true },
    {
        label: 'Áreas de Negocio',
        icon: 'pi pi-briefcase',
        items: [
            {
                label: 'Finanzas',
                icon: 'pi pi-fw pi-dollar',
                items: [
                    // TODO: de Finanzas el Excel solo cubre retornos y el reporte de transacciones,
                    // así que las 7 pantallas comparten FINANCE_READ. Pendiente de desglosar.
                    // { label: 'Analytics', icon: 'pi pi-fw pi-chart-bar', routerLink: ['/pages/finance/analytics'], section: 'FINANCE_READ' },
                    { label: 'Reportos', icon: 'pi pi-fw pi-file', routerLink: ['/pages/finance/reportos'], section: 'FINANCE_REPORTOS' },
                    { label: 'Retornos', icon: 'pi pi-fw pi-undo', routerLink: ['/pages/finance/retornos'], section: 'FINANCE_RETURNS' },
                    // { label: 'STP', icon: 'pi pi-fw pi-send', routerLink: ['/pages/finance/stp'], section: 'FINANCE_READ' },
                    { label: 'Facturacion', icon: 'pi pi-fw pi-receipt', routerLink: ['/pages/finance/invoice'], section: 'FINANCE_INVOICING' },
                    // { label: 'Cuentas de ingresos', icon: 'pi pi-fw pi-wallet', routerLink: ['/pages/finance/income'], section: 'FINANCE_READ' },
                    { label: 'Reportes de transacciones', icon: 'pi pi-fw pi-chart-line', routerLink: ['/pages/finance/transactions-report'], section: 'FINANCE_READ' }
                ]
            },
            {
                label: 'Operaciones',
                icon: 'pi pi-fw pi-cog',
                items: [
                    { label: 'Cargo y abono unitario', icon: 'pi pi-fw pi-money-bill', routerLink: ['/pages/operations/unit-movement'], section: 'OPERATIONS_EXECUTE' },
                    { label: 'Dashboard', icon: 'pi pi-fw pi-chart-bar', routerLink: ['/pages/operations/dashboard-auth'], section: 'OPERATIONS_EXECUTE' }
                ]
            },
            {
                label: 'Cumplimiento',
                icon: 'pi pi-fw pi-shield',
                items: [
                    // TODO: el Excel no tiene columnas para Cumplimiento; los 6 tabs comparten COMPLIANCE.
                    { label: 'Autenticación', icon: 'pi pi-fw pi-key', routerLink: ['/pages/compliance/auth'], section: 'COMPLIANCE' },
                    { label: 'Abonos', icon: 'pi pi-fw pi-arrow-circle-down', routerLink: ['/pages/compliance/payment'], section: 'COMPLIANCE' },
                    { label: 'Geolocalización', icon: 'pi pi-fw pi-map-marker', routerLink: ['/pages/compliance/geo'], section: 'COMPLIANCE' },
                    { label: 'listas', icon: 'pi pi-fw pi-ban', routerLink: ['/pages/compliance/pld'], section: 'COMPLIANCE' },
                    { label: 'Transaccionalidad', icon: 'pi pi-fw pi-gauge', routerLink: ['/pages/compliance/transactionality'], section: 'COMPLIANCE' },
                    { label: 'Clientes KYC', icon: 'pi pi-fw pi-id-card', routerLink: ['/pages/compliance/kyc-customers'], section: 'COMPLIANCE' }
                ]
            }
        ]
    },
    { separator: true },
    {
        label: 'Sistema',
        icon: 'pi pi-fw pi-users',
        items: [
            {
                label: 'Usuarios',
                icon: 'pi pi-fw pi-users',
                items: [
                    {
                        label: 'Lista',
                        icon: 'pi pi-fw pi-list',
                        routerLink: ['/pages/users'],
                        section: 'USERS_READ'
                    },
                    {
                        label: 'Crear',
                        icon: 'pi pi-fw pi-user-plus',
                        routerLink: ['/pages/users/create'],
                        section: 'USERS_WRITE'
                    }
                ]
            }
        ]
    }
];
